import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { setActiveTab } from "../../../redux/Actions/tabAcions";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import QrCodeDetail from "../../../components/QrCodeDetail";
import axios from "axios";
import { message } from "antd";

import Loader from "../../../components/Loader";
import {
  clearSettingsResult,
  getSingleUser,
  updateUserSettings,
} from "../../../redux/slices/clientSlice";
import { BACKEND_URL } from "../../../config";
import QRCodeGenerator from "../../QrGenerator";
import ValetLocationQrCode from "../../../components/ValetLocationQrCode";

const SERVICE_LABELS = {
  maids: "Attendants",
  valets: "Valet",
  bellmen: "Bell Desk",
  airbnb: "Airbnb",
};

const isServiceActive = (svc) =>
  Boolean(svc?.enabled && svc?.qrCode && svc?.botRef);

const EditClientSettings = ({ clientId }) => {
  const [updatedSettings, setUpdatedSettings] = useState(null);
  const [qrCodeModal, setQRCodeModal] = useState(false);
  const [selectedServiceConfig, setSelectedServiceConfig] = useState(null);
  const [uploadLoading, setUploadLoading] = useState({});
  const {
    loading,
    selectedUser: client,
    settingsLoading,
    settingsError,
    settingsSuccess,
  } = useSelector((state) => state.client);
  const activeTab = useSelector((state) => state.navigation.activeTab);

  const dispatch = useDispatch();

  const handleBackButton = () => {
    dispatch(setActiveTab("#dashboard"));
  };

  const handleQRCodeModalClose = () => {
    setQRCodeModal(false);
    setSelectedServiceConfig();
  };

  const openQRCodeModal = (config, isValet = false) => {
    setSelectedServiceConfig({ ...config, isValet });
    setQRCodeModal(true);
  };

  const patchService = (serviceKey, patch) => {
    setUpdatedSettings((prev) => ({
      ...prev,
      services: {
        ...prev.services,
        [serviceKey]: { ...prev.services[serviceKey], ...patch },
      },
    }));
  };

  const handleImageUpload = async (e, serviceKey) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadLoading((prev) => ({ ...prev, [serviceKey]: true }));

    try {
      const formData = new FormData();
      formData.append("qrImage", file);

      const { data } = await axios.post(
        `${BACKEND_URL}api/upload/qrCodes`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (data?.success && data?.fileUrl) {
        setUpdatedSettings((prev) => ({
          ...prev,
          services: {
            ...prev.services,
            [serviceKey]: {
              ...prev.services?.[serviceKey],
              qrCode: data.fileUrl,
            },
          },
        }));
        message.success("QR image uploaded");
      } else {
        message.error(data?.message || "Upload failed");
      }
    } catch (err) {
      console.error("Upload error:", err);
      message.error("Error uploading the image");
    } finally {
      setUploadLoading((prev) => ({ ...prev, [serviceKey]: false }));
      e.target.value = "";
    }
  };
  const handleValetLocationImageUpload = async (e, botRefId) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadLoading((prev) => ({ ...prev, [botRefId]: true }));

    try {
      const formData = new FormData();
      formData.append("qrImage", file);

      const { data } = await axios.post(
        `${BACKEND_URL}api/upload/qrCodes`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (data?.success && data?.fileUrl) {
        //  update state for this valet location
        setUpdatedSettings((prev) => ({
          ...prev,
          valetLocations: prev.valetLocations.map((loc) =>
            String(loc.botRefId) === String(botRefId)
              ? { ...loc, qrCode: data.fileUrl }
              : loc
          ),
        }));
        message.success("QR image uploaded");
      } else {
        message.error(data?.message || "Upload failed");
      }
    } catch (err) {
      console.error("Upload error:", err);
      message.error("Error uploading the image");
    } finally {
      setUploadLoading((prev) => ({ ...prev, [botRefId]: false }));
      e.target.value = "";
    }
  };

  const handleSave = async () => {
    if (!updatedSettings) return;
    const services = { ...updatedSettings.services };
    const valetLocations = [...updatedSettings.valetLocations];

    console.log("Valet locations before save", valetLocations);

    dispatch(
      updateUserSettings({
        id: updatedSettings._id,
        services,
        valetLocations,
      })
    );
  };

  useEffect(() => {
    if (!clientId) return;

    dispatch(clearSettingsResult());
    dispatch(getSingleUser(clientId));
  }, [clientId, dispatch, activeTab]);

  useEffect(() => {
    if (client) {
      setUpdatedSettings({
        ...client,
        services: { ...client.services },
        valetLocations: client.valetLocations.map((loc) => ({ ...loc })),
      });
    }
  }, [client]);

  useEffect(() => {
    let hideLoading;
    if (settingsLoading) {
      hideLoading = message.loading("Updating", 0);
    }
    if (settingsSuccess) {
      if (hideLoading) hideLoading();
      message.success("Updated!");
      // setEditing(false);
      dispatch(clearSettingsResult());
    }
    if (settingsError) {
      if (hideLoading) hideLoading();
      message.error(settingsError || "Failed to update");
    }
    return () => {
      if (hideLoading) hideLoading();
    };
  }, [settingsLoading, settingsError]);

  // Pick only the services this user actually has
  const activeServices = useMemo(() => {
    const services = updatedSettings?.services || {};
    return Object.entries(services)
      .filter(([, cfg]) => cfg?.enabled)
      .map(([key, cfg]) => ({
        key,
        label: SERVICE_LABELS[key] || key,
        cfg,
      }));
  }, [updatedSettings]);

  const hasValetLocations = (client?.valetLocations || []).length > 0;

  return (
    <>
      {qrCodeModal && selectedServiceConfig && (
        <QRCodeGenerator
          show={qrCodeModal}
          onHide={handleQRCodeModalClose}
          serviceConfig={selectedServiceConfig}
          upload={handleImageUpload} // for services
          locationQrUpload={handleValetLocationImageUpload} // for valet locations

          // valetLocationName={valetLocationName}
          // locationQrUpload={handleValetLocationImageUpload}
          // index={locationIndex}
        />
      )}
      <Row className="p-4">
        <Col className="p-0">
          {/* ----------------------------------------------------------------- Back button ---------------------------------------------------------------- */}
          <Row className="mb-1 text-primary">
            <Col className="hover-effect" xs="auto" onClick={handleBackButton}>
              <FontAwesomeIcon icon={faChevronLeft} className=" me-2" />
              <span className="fw-semibold">Dashboard</span>
            </Col>
          </Row>

          {/* ----------------------------------------------------------------- QR code settings ------------------------------------------------------------*/}
          {loading ? (
            <Loader />
          ) : (
            <Row className="g-4">
              <h4 className="text-primary text-center mb-0 fw-semibold">
                QR Code Settings
              </h4>

              {activeServices.length === 0 && !hasValetLocations && (
                <Col className="text-center">
                  <div className="text-muted">
                    No active services found for this user.
                  </div>
                </Col>
              )}

              {activeServices.map(({ key, label, cfg }) => (
                <Col key={key} xs={12}>
                  <QrCodeDetail
                    setQRCodeModal={setQRCodeModal}
                    openQRCodeModal={openQRCodeModal}
                    serviceConfig={{
                      title: label,
                      ...cfg,
                      onChange: (patch) => patchService(key, patch),
                      onSave: handleSave,
                      serviceKey: key,
                      onUpload: handleImageUpload,
                      uploading: !!uploadLoading[key],
                    }}
                  />
                </Col>
              ))}

              {/*Show valet locations block if present */}
              {hasValetLocations &&
                updatedSettings?.valetLocations?.map((loc) => (
                  <Col key={loc._id} xs={12}>
                    <ValetLocationQrCode
                      location={loc}
                      onUpload={handleValetLocationImageUpload}
                      uploading={!!uploadLoading[loc.botRefId]}
                      onSave={handleSave}
                      openQRCodeModal={openQRCodeModal}
                      // onUpload={handleValetLocationImageUpload}
                      // uploading={!!uploadLoading[loc._id]}
                    />
                  </Col>
                ))}
            </Row>
          )}
        </Col>
      </Row>
    </>
  );
};

export default EditClientSettings;
